import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Space, Select, Input, InputNumber, Checkbox, Progress, Tag, List, Typography, Row, Col, message, Slider, Avatar,
} from 'antd';
import {
  CommentOutlined, ThunderboltOutlined, SendOutlined, DeleteOutlined, PlusOutlined, SyncOutlined, UserOutlined, SearchOutlined, SettingOutlined, HolderOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiRequest, apiPost } from '../services/api';
import { getEmployeeList, BotEmployee } from '../services/botEmployeeApi';

const { Title, Text } = Typography;
const { TextArea } = Input;

// 商务风格配色
const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  // 兼容旧代码的别名
  cyan: '#1677ff',
  purple: '#722ed1',
  pink: '#ff4d4f',
  green: '#52c41a',
  orange: '#faad14',
  yellow: '#faad14',
  darkBg: '#f5f5f5',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

const LANGUAGE_OPTIONS = [
  { code: 'zh-CN', label: '简体中文' }, { code: 'zh-TW', label: '繁體中文' }, { code: 'en', label: 'English' },
  { code: 'ko', label: '한국어' }, { code: 'ja', label: '日本語' },
];

const AI_PROVIDER_OPTIONS = [{ code: 'deepseek', label: 'DeepSeek' }, { code: 'claude', label: 'Claude' }];

interface Comment { seq: number; content: string; replyTo: number | null; botId?: number; }
interface TaskStatus { taskId: string; status: string; totalCount: number; sentCount: number; failedCount: number; followedCount: number; progress: string; details: any[]; }
interface TalkInfo {
  talkId: number;
  content: string;
  author?: { userId: number; name: string; avatar: string; isOfficial?: number; };
}

// 简洁页面标题组件
const PageHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; color?: string }> = ({ icon, title, subtitle, color = COLORS.primary }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <div style={{
        width: 48, height: 48,
        background: `${color}10`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, color: color,
      }}>{icon}</div>
      <div>
        <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{title}</Title>
        {subtitle && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{subtitle}</Text>}
      </div>
    </div>
    <div style={{
      width: 120, height: 3, marginLeft: 60,
      background: `linear-gradient(90deg, ${color}, ${color}66, transparent)`,
      borderRadius: 2,
    }} />
  </div>
);

// 简洁卡片组件
const SimpleCard: React.FC<{ title?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties; accentColor?: string }> = ({ title, extra, children, style, accentColor = COLORS.primary }) => (
  <Card
    title={title ? <Text style={{ fontWeight: 600, fontSize: 16 }}>{title}</Text> : undefined}
    extra={extra}
    style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.borderDark}`,
      borderRadius: 12,
      overflow: 'hidden',
      ...style,
    }}
    styles={{
      header: title ? {
        borderBottom: `1px solid ${COLORS.borderDark}`,
        background: `${accentColor}08`,
      } : undefined,
      body: { padding: 24 },
    }}
  >
    {children}
  </Card>
);

// 可拖拽评论项组件
const SortableCommentItem: React.FC<{
  id: string;
  comment: Comment;
  botEmployees: BotEmployee[];
  onContentChange: (value: string) => void;
  onBotChange: (botId: number | undefined) => void;
  onDelete: () => void;
}> = ({ id, comment, botEmployees, onContentChange, onBotChange, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <List.Item ref={setNodeRef} style={{ ...style, padding: '12px 0', border: 'none' }} {...attributes}>
      <Space style={{ width: '100%' }} align="start">
        <div {...listeners} style={{ cursor: 'grab', padding: '8px 4px' }}>
          <HolderOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />
        </div>
        <Tag color="purple" style={{ minWidth: 32, textAlign: 'center' }}>{comment.seq}</Tag>
        <TextArea value={comment.content} onChange={(e) => onContentChange(e.target.value)} rows={2} style={{ flex: 1 }} />
        {botEmployees.length > 0 && (
          <Select
            value={comment.botId}
            onChange={(v) => onBotChange(v)}
            allowClear
            placeholder="自动分配"
            style={{ width: 150 }}
            options={botEmployees.map(b => ({
              value: b.botId,
              label: (
                <Space size={4}>
                  <Avatar src={b.avatar} icon={<UserOutlined />} size={18} />
                  <span>{b.userName || `Bot#${b.botId}`}</span>
                </Space>
              ),
            }))}
          />
        )}
        <Button type="text" danger icon={<DeleteOutlined />} onClick={onDelete} />
      </Space>
    </List.Item>
  );
};

const PostComment: React.FC = () => {
  const [talkId, setTalkId] = useState('');
  const [count, setCount] = useState(5);
  const [hasContext, setHasContext] = useState(false);
  const [language, setLanguage] = useState('zh-CN');
  const [minLength, setMinLength] = useState(10);
  const [maxLength, setMaxLength] = useState(100);
  const [followRate, setFollowRate] = useState(60);
  const [aiType, setAiType] = useState('deepseek');
  const [talkInfo, setTalkInfo] = useState<TalkInfo | null>(null);
  const [existingComments, setExistingComments] = useState<any[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [currentTaskId, setCurrentTaskId] = useState('');
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [botEmployees, setBotEmployees] = useState<BotEmployee[]>([]);

  // 加载专属机器人员工列表
  useEffect(() => {
    getEmployeeList().then(setBotEmployees).catch(() => setBotEmployees([]));
  }, []);

  // 拖拽排序
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setComments((items) => {
        const oldIndex = items.findIndex((_, i) => `comment-${i}` === active.id);
        const newIndex = items.findIndex((_, i) => `comment-${i}` === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((item, i) => ({ ...item, seq: i + 1 }));
      });
    }
  };

  const handleGetDetail = async () => {
    if (!talkId) { message.warning('请输入帖子ID'); return; }
    setLoadingDetail(true);
    try {
      const data = await apiRequest<any>(`/api/post-comment/detail`, {
        method: 'POST',
        body: JSON.stringify({ talkId: parseInt(talkId) }),
      });
      if (data.code === 0) {
        // 检查是否有帖子数据
        if (!data.data?.talk && (!data.data?.list || data.data.list.length === 0)) {
          message.warning('帖子不存在或已被删除');
          setTalkInfo(null);
          setExistingComments([]);
          return;
        }
        setTalkInfo(data.data.talk);
        setExistingComments(data.data.comments || []);
        message.success('获取成功');
      }
      else { message.error(data.msg || '获取失败'); }
    } catch (e: any) { message.error('请求失败: ' + e.message); }
    finally { setLoadingDetail(false); }
  };

  const handleGenerate = async () => {
    if (!talkId) { message.warning('请先输入帖子ID'); return; }
    setGenerating(true);
    try {
      const data = await apiRequest<any>(`/api/post-comment/generate`, {
        method: 'POST',
        body: JSON.stringify({ talkId: parseInt(talkId), count, language, hasContext, minLength, maxLength, aiType }),
      });
      if (data.code === 0) { setTalkInfo(data.data.talk); setExistingComments(data.data.existingComments || []); setComments(data.data.generatedComments || []); message.success('生成成功'); }
      else { message.error(data.msg || '生成失败'); }
    } catch (e: any) { message.error('请求失败: ' + e.message); }
    finally { setGenerating(false); }
  };

  const handleSend = async () => {
    if (comments.length === 0) { message.warning('请先生成评论'); return; }
    setSending(true);
    try {
      const data = await apiRequest<any>(`/api/post-comment/send`, {
        method: 'POST',
        body: JSON.stringify({
          talkId: parseInt(talkId), authorUserId: talkInfo?.author?.userId,
          comments: comments.map(c => ({ seq: c.seq, content: c.content, parentId: c.replyTo, botId: c.botId || undefined })),
          followRate, intervalMin: 5, intervalMax: 15,
        }),
      });
      if (data.code === 0) { setCurrentTaskId(data.data.taskId); setTaskStatus(null); message.success('发送任务已创建'); }
      else { message.error(data.msg || '发送失败'); }
    } catch (e: any) { message.error('请求失败: ' + e.message); }
    finally { setSending(false); }
  };

  const fetchTaskStatus = useCallback(async () => {
    if (!currentTaskId) return;
    try {
      const data = await apiRequest<any>(`/api/post-comment/task/${currentTaskId}`, {});
      if (data.code === 0) { setTaskStatus(data.data); }
    } catch (e) { console.error('查询任务状态失败', e); }
  }, [currentTaskId]);

  useEffect(() => {
    if (!currentTaskId) return;
    fetchTaskStatus();
    const interval = setInterval(() => {
      if (['completed', 'failed', 'cancelled'].includes(taskStatus?.status || '')) { clearInterval(interval); return; }
      fetchTaskStatus();
    }, 2000);
    return () => clearInterval(interval);
  }, [currentTaskId, taskStatus?.status, fetchTaskStatus]);

  // 修改发送人
  const handleChangeSender = async (detailId: number, botId: number) => {
    try {
      const data = await apiPost<any>(`/api/post-comment/detail/${detailId}/change-sender`, { botId });
      if (data.code === 0) {
        message.success('发送人修改成功');
        fetchTaskStatus();
      } else {
        message.error(data.msg || '修改失败');
      }
    } catch (e: any) { message.error('修改失败: ' + e.message); }
  };

  const getStatusColor = (status: string) => ({ pending: '#6b7280', running: COLORS.cyan, completed: COLORS.green, failed: COLORS.pink, cancelled: COLORS.orange, sent: COLORS.green }[status] || '#6b7280');
  const getStatusText = (status: string) => ({ pending: '等待中', running: '执行中', completed: '已完成', failed: '失败', cancelled: '已取消', sent: '已发送' }[status] || status);

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader icon={<CommentOutlined />} title="帖子评论" subtitle="批量发送帖子评论，支持AI生成内容" color={COLORS.success} />

      <Row gutter={24}>
        <Col xs={24} lg={10}>
          <SimpleCard title={<Space><SettingOutlined style={{ color: COLORS.success }} /><span>1. 参数设置</span></Space>} style={{ marginBottom: 24 }} accentColor={COLORS.success}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block', fontSize: 13 }}>帖子ID</Text>
                <Space.Compact style={{ width: '100%' }}>
                  <Input value={talkId} onChange={(e) => setTalkId(e.target.value)} placeholder="输入帖子ID" />
                  <Button type="primary" icon={<SearchOutlined />} onClick={handleGetDetail} loading={loadingDetail}>获取详情</Button>
                </Space.Compact>
              </div>
              <Row gutter={16}>
                <Col span={8}>
                  <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block', fontSize: 13 }}>评论数量</Text>
                  <InputNumber value={count} onChange={(v) => setCount(v || 5)} min={1} max={100} style={{ width: '100%' }} />
                </Col>
                <Col span={8}>
                  <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block', fontSize: 13 }}>输出语言</Text>
                  <Select value={language} onChange={setLanguage} style={{ width: '100%' }} options={LANGUAGE_OPTIONS.map(l => ({ value: l.code, label: l.label }))} />
                </Col>
                <Col span={8}>
                  <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block', fontSize: 13 }}>AI提供商</Text>
                  <Select value={aiType} onChange={setAiType} style={{ width: '100%' }} options={AI_PROVIDER_OPTIONS.map(p => ({ value: p.code, label: p.label }))} />
                </Col>
              </Row>
              <div>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block', fontSize: 13 }}>字数范围: <span style={{ color: COLORS.success, fontWeight: 600 }}>{minLength} - {maxLength}</span></Text>
                <Slider range value={[minLength, maxLength]} onChange={([min, max]) => { setMinLength(min); setMaxLength(max); }} min={1} max={200} />
              </div>
              <div>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block', fontSize: 13 }}>关注比例: <span style={{ color: COLORS.secondary, fontWeight: 600 }}>{followRate}%</span></Text>
                <Slider value={followRate} onChange={setFollowRate} min={0} max={100} />
              </div>
              <Checkbox checked={hasContext} onChange={(e) => setHasContext(e.target.checked)}><Text style={{ color: 'rgba(0, 0, 0, 0.65)' }}>评论间有上下文关系</Text></Checkbox>
              <Button type="primary" icon={<ThunderboltOutlined />} onClick={handleGenerate} loading={generating} disabled={!talkId} block size="large" style={{ height: 48, fontWeight: 600, background: COLORS.success, borderColor: COLORS.success }}>
                AI 生成评论
              </Button>
            </Space>
          </SimpleCard>
        </Col>

        <Col xs={24} lg={14}>
          {talkInfo && (
            <SimpleCard title={<Space><UserOutlined style={{ color: COLORS.primary }} /><span>2. 帖子详情</span></Space>} style={{ marginBottom: 24 }} accentColor={COLORS.primary}>
              <Space align="start" style={{ marginBottom: 16, padding: 16, background: COLORS.darkBg, borderRadius: 8, border: `1px solid ${COLORS.borderDark}`, width: '100%' }}>
                <Avatar src={talkInfo.author?.avatar} icon={<UserOutlined />} size={48} />
                <div style={{ flex: 1 }}>
                  <Text style={{ fontWeight: 600, fontSize: 16 }}>{talkInfo.author?.name || '未知用户'}</Text>
                  <Text style={{ color: 'rgba(0, 0, 0, 0.65)', display: 'block', marginTop: 8, lineHeight: 1.6 }}>{talkInfo.content}</Text>
                </div>
              </Space>
              {existingComments.length > 0 && (
                <div style={{ borderTop: `1px solid ${COLORS.borderDark}`, paddingTop: 16 }}>
                  <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 12, display: 'block', fontWeight: 500 }}>现有评论 <Tag color="blue">{existingComments.length}</Tag></Text>
                  <List size="small" dataSource={existingComments.slice(0, 5)} renderItem={(c: any) => (
                    <List.Item style={{ padding: '8px 12px', border: 'none', background: COLORS.darkBg, borderRadius: 6, marginBottom: 6 }}>
                      <Text style={{ color: 'rgba(0, 0, 0, 0.65)' }}><Text style={{ fontWeight: 500 }}>{c.nickName || '用户'}</Text>: {c.content}</Text>
                    </List.Item>
                  )} />
                  {existingComments.length > 5 && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginTop: 8, display: 'block' }}>...还有 {existingComments.length - 5} 条</Text>}
                </div>
              )}
            </SimpleCard>
          )}

          {comments.length > 0 && (
            <SimpleCard
              title={<Space><SendOutlined style={{ color: COLORS.secondary }} /><span>3. AI生成评论</span><Tag color="purple">{comments.length}</Tag></Space>}
              extra={<Button icon={<PlusOutlined />} onClick={() => setComments([...comments, { seq: comments.length + 1, content: '', replyTo: null }])}>添加</Button>}
              style={{ marginBottom: 24 }}
              accentColor={COLORS.secondary}
            >
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={comments.map((_, i) => `comment-${i}`)} strategy={verticalListSortingStrategy}>
                  <List dataSource={comments} renderItem={(comment, index) => (
                    <SortableCommentItem
                      key={`comment-${index}`}
                      id={`comment-${index}`}
                      comment={comment}
                      botEmployees={botEmployees}
                      onContentChange={(value) => { const nc = [...comments]; nc[index] = { ...nc[index], content: value }; setComments(nc); }}
                      onBotChange={(botId) => { const nc = [...comments]; nc[index] = { ...nc[index], botId }; setComments(nc); }}
                      onDelete={() => setComments(comments.filter((_, i) => i !== index))}
                    />
                  )} />
                </SortableContext>
              </DndContext>
              <Button type="primary" icon={<SendOutlined />} onClick={handleSend} loading={sending} disabled={comments.length === 0} block size="large" style={{ marginTop: 16, height: 48, fontWeight: 600, background: COLORS.secondary, borderColor: COLORS.secondary }}>
                确认发送
              </Button>
            </SimpleCard>
          )}
        </Col>
      </Row>

      {currentTaskId && (
        <SimpleCard title={<Space><SyncOutlined spin={taskStatus?.status === 'running'} style={{ color: COLORS.warning }} /><span>4. 任务状态</span></Space>} extra={<Button icon={<SyncOutlined />} onClick={fetchTaskStatus}>刷新</Button>} accentColor={COLORS.warning}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Row gutter={16}>
              <Col span={6}><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>任务ID:</Text><Text style={{ marginLeft: 8 }} copyable>{currentTaskId}</Text></Col>
              {taskStatus && (<>
                <Col span={6}><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>状态:</Text><Tag color={getStatusColor(taskStatus.status)} style={{ marginLeft: 8, borderRadius: 4 }}>{getStatusText(taskStatus.status)}</Tag></Col>
                <Col span={6}><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>发送:</Text><Text style={{ color: COLORS.success, marginLeft: 8, fontWeight: 600 }}>{taskStatus.sentCount}/{taskStatus.totalCount}</Text></Col>
                <Col span={6}><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>关注:</Text><Text style={{ color: COLORS.secondary, marginLeft: 8, fontWeight: 600 }}>{taskStatus.followedCount}</Text></Col>
              </>)}
            </Row>
            {taskStatus && <Progress percent={parseInt(taskStatus.progress) || 0} strokeColor={COLORS.warning} strokeWidth={10} />}
            {taskStatus?.details && taskStatus.details.length > 0 && (
              <List size="small" dataSource={taskStatus.details} renderItem={(d: any) => (
                <List.Item style={{ padding: '8px 12px', background: COLORS.darkBg, borderRadius: 6, marginBottom: 6, border: 'none' }}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <Tag color="purple">{d.seq}</Tag>
                      <Text style={{ color: 'rgba(0,0,0,0.65)', maxWidth: 300 }} ellipsis>{d.content}</Text>
                    </Space>
                    <Space>
                      <Text style={{ color: 'rgba(0,0,0,0.45)' }}>{d.botName || '未分配'}</Text>
                      {d.status === 'pending' && botEmployees.length > 0 ? (
                        <Select
                          value={d.botId}
                          onChange={(v) => handleChangeSender(d.detailId, v)}
                          style={{ width: 140 }}
                          size="small"
                          placeholder="更换发送人"
                          options={botEmployees.map(b => ({
                            value: b.botId,
                            label: b.userName || `Bot#${b.botId}`,
                          }))}
                        />
                      ) : (
                        <Tag color={getStatusColor(d.status)} style={{ borderRadius: 4 }}>{getStatusText(d.status)}</Tag>
                      )}
                    </Space>
                  </Space>
                </List.Item>
              )} />
            )}
          </Space>
        </SimpleCard>
      )}
    </div>
  );
};

export default PostComment;
